import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TooltipProvider } from '@predoc/cow';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/hooks';
import { createEfaxColumns } from '../columns/efaxColumns';
import { useEfaxQueue } from '../hooks/useEfaxQueue';
import { useEfaxSearch } from '../hooks/useEfaxSearch';
import type { EfaxQueueItem } from '../types';
import { getPatientKey } from '../utils/efax-item-helpers';
import { EfaxDocumentViewer } from './EfaxDocumentViewer';
import { EfaxQueueTable } from './EfaxQueueTable';
import { EfaxSearchBar } from './EfaxSearchBar';

export const EfaxPage = () => {
  usePageTitle('Faxes received by Anonymous Record Upload');
  const navigate = useNavigate();

  const {
    search,
    setSearch,
    appliedSearch,
    hasSearched,
    hasUnappliedChanges,
    onSearch,
    onClear,
  } = useEfaxSearch();

  const {
    items,
    totalCount,
    isFetching,
    isError,
    sorting,
    onSortingChange,
    pagination,
    setPagination,
    resetPagination,
    resetPaginationAndSorting,
  } = useEfaxQueue({ appliedSearch, hasSearched });

  const [selectedItem, setSelectedItem] = useState<EfaxQueueItem | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    string | undefined
  >(undefined);

  const columns = useMemo(
    () =>
      createEfaxColumns(
        (item, documentId) => {
          setSelectedItem(item);
          setSelectedDocumentId(documentId);
        },
        (item) => {
          navigate(`/patients/${getPatientKey(item)}`);
        },
      ),
    [navigate],
  );

  const handleSearch = () => {
    onSearch();
    resetPagination();
  };

  const handleClear = () => {
    onClear();
    resetPaginationAndSorting();
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-50">
        <PageHeader>
          <h1 className="text-3xl font-bold">
            Faxes received by Anonymous Record Upload
          </h1>
        </PageHeader>

        <div className="mb-4 px-6 pt-4">
          <div className="w-full max-w-full space-y-4">
            <EfaxSearchBar
              search={search}
              hasSearched={hasSearched}
              onChange={setSearch}
              onSearch={handleSearch}
              onClear={handleClear}
              hasUnappliedChanges={hasUnappliedChanges}
            />
            <div
              className={cn(
                'border-border w-full overflow-hidden rounded-lg border bg-white shadow-sm',
                hasSearched && totalCount > 0
                  ? ''
                  : 'bg-transparent shadow-none',
              )}
            >
              <EfaxQueueTable
                columns={columns}
                hasSearched={hasSearched}
                isFetching={isFetching}
                isError={isError}
                items={items}
                totalCount={totalCount}
                sorting={sorting}
                onSortingChange={onSortingChange}
                pagination={pagination}
                setPagination={setPagination}
              />
            </div>
          </div>
        </div>

        <EfaxDocumentViewer
          item={selectedItem}
          open={!!selectedItem}
          initialDocumentId={selectedDocumentId}
          onClose={() => {
            setSelectedItem(null);
            setSelectedDocumentId(undefined);
          }}
        />
      </div>
    </TooltipProvider>
  );
};
